// Absendernummer („Anrufen mit“, wie im Telefon-Popover von Close): die Nummer, die der Angerufene sieht. Seit
// Design-Runde 2 steht sie im Headset-Menü oben rechts (`HeadsetPruefen.tsx`), nicht mehr als eigener Knopf in der
// Kopfzeile. Gewählt wird unter den Nummern, die der Nutzer verwenden darf (`GET /api/telefonie/nummern`);
// gespeichert wie in Einstellungen > Telefonie (`PUT /api/telefonie/einstellungen`).
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { useTelefonie } from "../telefonie";
import { tel, type Nummer } from "../telefonie/api";
import { Icon } from "./Icon";
import { useMeldung } from "./Meldungen";
import { telefonText } from "./Telefonnummer";

export function useAbsender() {
  const t = useTelefonie();
  const { nutzer } = useAuth();
  const meldung = useMeldung();
  const [nummern, setNummern] = useState<Nummer[]>([]);
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const bereit = t.status !== "nicht_eingerichtet";

  useEffect(() => {
    if (!bereit) return;
    let abgebrochen = false;
    tel.nummern().then((r) => !abgebrochen && setNummern(r.data)).catch(() => undefined);
    return () => {
      abgebrochen = true;
    };
  }, [bereit]);

  const aktuell = gewaehlt ?? t.info?.caller_id ?? nutzer?.caller_id ?? null;

  const waehlen = useCallback(
    async (nummer: string) => {
      try {
        await tel.einstellungenAendern({ caller_id: nummer });
        setGewaehlt(nummer);
        meldung.ok(`Absendernummer: ${telefonText(nummer)}`);
        void t.neuLaden();
      } catch (e) {
        meldung.fehler(e, "Absendernummer nicht geändert");
      }
    },
    [meldung, t],
  );
  return { bereit, nummern, aktuell, waehlen };
}

/** Liste „Anrufen mit“ für das Headset-Menü: ein Klick wählt die Absendernummer, der Haken steht an der aktuellen. */
export function AbsenderAuswahl() {
  const { bereit, nummern, aktuell, waehlen } = useAbsender();
  if (!bereit) return <p className="hs-klein">Die Telefonie ist noch nicht eingerichtet.</p>;
  if (!nummern.length && !aktuell) return <p className="hs-klein">Keine Absendernummer hinterlegt.</p>;
  const liste = [...nummern.map((n) => ({ nummer: n.number, label: n.label }))];
  if (aktuell && !liste.some((n) => n.nummer === aktuell)) liste.push({ nummer: aktuell, label: "eigene Nummer" });
  return (
    <div className="hs-liste" role="radiogroup" aria-label="Absendernummer">
      {liste.map((n) => {
        const an = n.nummer === aktuell;
        return (
          <button
            key={n.nummer}
            type="button"
            role="radio"
            aria-checked={an}
            className={`hs-nummer${an ? " hs-nummer--an" : ""}`}
            onClick={() => !an && void waehlen(n.nummer)}
          >
            <span className="hs-nummer__text">
              <span className="hs-nummer__zahl">{telefonText(n.nummer)}</span>
              {n.label ? <span className="hs-nummer__label">{n.label}</span> : null}
            </span>
            {an ? <Icon name="haken" groesse={15} /> : null}
          </button>
        );
      })}
    </div>
  );
}
